'use client';

import { datadogRum } from '@datadog/browser-rum';
import type { AnswerState } from '@repo/contracts/cardfinder/funnel/types';
import { useIdentityContext } from '@repo/platform-analytics/IdentityContext';
import { env } from '@repo/platform-env';
import { useRouter } from 'next/navigation';
import { type TransitionStartFunction, useEffect } from 'react';
import type { BranchId } from '../../../config/constants';
import { ENTRY_STEP_ID } from '../../funnel/constants';
import { saveFunnelSession } from '../../saveFunnelSession';
import { readLocalSession, shouldRotateLocalSession } from '../../storage';
import {
  useAnswerDispatch,
  useHasSyncedOnLoginRef,
  useIsAnswerHydrated,
  useSubmittedAnswers,
} from '../hooks';
import { abandonFunnelRun } from './abandonFunnelRun';

const hasAnsweredBeyondEntry = (answers: AnswerState) =>
  Object.keys(answers).some(stepId => stepId !== ENTRY_STEP_ID);

/**
 * Catches up an unfinished run's session write once on login, and only for
 * an unfinished run.
 */
export function useFunnelSessionSync(
  branchId: BranchId | null,
  funnelConfigVersion: string,
  startFunnelTransition: TransitionStartFunction,
) {
  const isSessionPersistenceEnabled = env.NEXT_PUBLIC_ENABLE_SESSION_PERSISTENCE;
  const { isLoggedIn } = useIdentityContext();
  const router = useRouter();
  const dispatch = useAnswerDispatch();
  const isHydrated = useIsAnswerHydrated();
  const submittedAnswers = useSubmittedAnswers();
  const hasSyncedOnLoginRef = useHasSyncedOnLoginRef();

  useEffect(() => {
    if (!branchId || !isSessionPersistenceEnabled || !isLoggedIn || !isHydrated) return;
    if (hasSyncedOnLoginRef.current) return;
    // Selecting a primary goal alone isn't progress: the session should only
    // be created once the user has also answered the first question.

    // Check only against submitted answers, not the current answers.
    // The answers in the store contain transient answers (not yet submitted)
    if (!hasAnsweredBeyondEntry(submittedAnswers)) return;
    if (shouldRotateLocalSession(readLocalSession())) return;

    hasSyncedOnLoginRef.current = true;
    // Runs as a submit so the funnel blocks new submissions until it resolves.
    startFunnelTransition(async () => {
      const { result } = await saveFunnelSession(submittedAnswers, { funnelConfigVersion });
      if (result === 'version-mismatch') abandonFunnelRun(dispatch, router);
      if (result === 'error') {
        datadogRum.addError(new Error('Login catch-up session save failed'), {
          branchId,
          funnelConfigVersion,
        });
      }
    });
  }, [
    branchId,
    isSessionPersistenceEnabled,
    isLoggedIn,
    isHydrated,
    submittedAnswers,
    funnelConfigVersion,
    hasSyncedOnLoginRef,
    dispatch,
    router,
    startFunnelTransition,
  ]);
}
